import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { api, Match, Goal, Weather } from '../api/client';
import { Flag, refDisplay } from './TeamBadge';
import { venue } from '../lib/venues';
import { weatherInfo } from '../lib/weather';

// Pizarra de estadio a pantalla COMPLETA para un partido EN VIVO (dispositivo vertical):
// ARRIBA (media pantalla) banderas + marcador + goleadores; ABAJO (media pantalla) la info
// del partido (minuto grande, sede y clima del lugar). ✕ vuelve a la ficha.
export function StadiumBoard({ match, onClose }: { match: Match; onClose: () => void }) {
  const t1 = refDisplay(match.team1);
  const t2 = refDisplay(match.team2);
  const [a, b] = match.score?.ft ?? [0, 0];
  const v = venue(match.ground);

  const [weather, setWeather] = useState<Weather | null>(null);
  useEffect(() => {
    if (!v) return;
    api.weather(v.lat, v.lon).then(setWeather).catch(() => {});
  }, [v?.ground, v]);

  const w = weather?.ok && weather.code != null ? weatherInfo(weather.code) : null;

  return (
    <Board>
      <CloseX onClick={onClose} aria-label="Cerrar">
        ✕
      </CloseX>

      <Top>
        <div className="board">
          <div className="team">
            <Flag team={t1.team} size={66} />
            <div className="nm">{t1.text}</div>
          </div>
          <div className="score">
            {a}
            <span className="sep">–</span>
            {b}
          </div>
          <div className="team">
            <Flag team={t2.team} size={66} />
            <div className="nm">{t2.text}</div>
          </div>
        </div>

        {(match.goals1.length > 0 || match.goals2.length > 0) && (
          <div className="goals">
            <div className="col left">
              {match.goals1.map((g, i) => (
                <span key={i}>⚽ {goalText(g)}</span>
              ))}
            </div>
            <div className="col right">
              {match.goals2.map((g, i) => (
                <span key={i}>{goalText(g)} ⚽</span>
              ))}
            </div>
          </div>
        )}
      </Top>

      <Bottom>
        <div className="live">
          <span className="dot" /> EN VIVO
        </div>
        <div className="min">{match.live?.clock ?? "—'"}</div>
        {v && (
          <div className="venue">
            📍 {v.stadium} · {v.city} {v.flag}
          </div>
        )}
        {w && (
          <div className="wx">
            {w.emoji} {weather!.temp}°<span className="wxt"> {w.text}</span>
          </div>
        )}
      </Bottom>
    </Board>
  );
}

function goalText(g: Goal): string {
  const tag = g.penalty ? ' (pen)' : g.owngoal ? ' (e/c)' : '';
  return `${g.minute ?? ''}' ${g.name}${tag}`;
}

const Board = styled.div`
  position: fixed;
  inset: 0;
  z-index: 900;
  display: flex;
  flex-direction: column;
  color: #fff;
  overflow: hidden;
`;

// Mitad de arriba: marcador + banderas + goleadores. Fondo de cancha de noche.
const Top = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 22px;
  padding: calc(env(safe-area-inset-top, 0px) + 24px) 18px 18px;
  overflow-y: auto;
  background:
    radial-gradient(120% 70% at 50% 0%, rgba(26, 168, 106, 0.26), transparent 62%),
    linear-gradient(180deg, #03090d, #06160f);

  .board {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 10px;
  }
  .team {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }
  .team .nm {
    font-weight: 800;
    font-size: 1.2rem;
    text-align: center;
  }
  .score {
    font-size: 5rem;
    font-weight: 900;
    line-height: 0.9;
    font-variant-numeric: tabular-nums;
    display: flex;
    align-items: center;
    gap: 8px;
    text-shadow: 0 4px 22px rgba(0, 0, 0, 0.5);
  }
  .score .sep { opacity: 0.4; font-size: 3rem; }

  .goals {
    width: 100%;
    max-width: 420px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px 16px;
    font-size: 1.02rem;
    color: rgba(255, 255, 255, 0.85);
  }
  .goals .col { display: flex; flex-direction: column; gap: 4px; }
  .goals .right { text-align: right; }
`;

// Mitad de abajo: info del partido. Fondo verde (franja de color) para diferenciarla.
const Bottom = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 18px calc(env(safe-area-inset-bottom, 0px) + 18px);
  text-align: center;
  background: linear-gradient(180deg, ${({ theme }) => theme.colors.accentDark}, ${({ theme }) => theme.colors.accent});
  box-shadow: 0 -8px 30px rgba(0, 0, 0, 0.5);

  .live {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 0.78rem;
    font-weight: 800;
    letter-spacing: 1.5px;
  }
  .live .dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #ff4d4d;
    animation: boardPulse 1.2s infinite;
  }
  .min {
    font-size: 4.4rem;
    font-weight: 900;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    text-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
  }
  /* Sede x2 y clima x4 (respecto del tamaño base), por pedido. */
  .venue { font-size: 1.8rem; font-weight: 700; margin-top: 6px; }
  .wx { font-size: 3.4rem; font-weight: 800; line-height: 1.05; margin-top: 4px; white-space: nowrap; }
  .wx .wxt { font-size: 1.4rem; font-weight: 600; opacity: 0.92; }

  @keyframes boardPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.2; }
  }
`;

const CloseX = styled.button`
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + 12px);
  right: 14px;
  z-index: 2;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  border-radius: 999px;
  width: 40px;
  height: 40px;
  font-size: 1.15rem;
`;
